import providersService from '../services/providers';
import { isStandardProvider } from '../constants/providers';
import { queryApi } from '../hooks/useGetPublicIdpDetails';
import { AppConfig } from '../types/app';
import { IdProvider } from '../types/auth';
import logger from '../utils/logger';

/**
 * Resolve an IDP name into a fully-formed {@link IdProvider} suitable for
 * federated sign-in in the simplified login flow.
 *
 * Standard providers (Builder ID, LWA, OTP, AmazonFederate, ...) always live in
 * the main Gandalf user pool, so a bare `{ idp, url }` from
 * `transformNameToIdProvider` is sufficient for them.
 *
 * Non-standard (ESSO/enterprise) providers may be configured in a user pool
 * other than the main Gandalf one. For those we must fetch the public IDP
 * details and carry `userPoolId`/`clientId`/`idpType` on the provider so that
 * `setAmplifyConfig` targets the correct (non-main) pool. Without this the
 * federation is attempted against the main pool and fails.
 *
 * Falls back to `transformNameToIdProvider` when the IDP is standard, when the
 * lookup returns no `authEndpoint`, or when the lookup errors — matching the
 * graceful-degradation behavior used elsewhere in this flow.
 *
 * This helper is owned by the simplified login flow. It reuses the shared,
 * read-only `queryApi` but intentionally lives outside `useGetPublicIdpDetails`
 * so that classic sign-in and other consumers of that hook are unaffected.
 */
export async function resolveIdProvider(
  config: AppConfig,
  idpName: string
): Promise<IdProvider> {
  const fallback = providersService.transformNameToIdProvider(
    idpName,
    config.gandalfDomain
  );

  if (isStandardProvider(idpName)) {
    return fallback;
  }

  try {
    const details = await queryApi(config, idpName);
    if (!details?.authEndpoint) {
      return fallback;
    }
    return {
      idp: idpName,
      url: providersService.getIdpUrl(idpName, details.authEndpoint),
      userPoolId: details.userPoolId,
      clientId: details.clientId,
      idpType: details.idpType,
      lastUsedIdpDisplayName: details.displayName,
    };
  } catch (error) {
    logger.debug(error);
    return fallback;
  }
}
